import {
    Component,
    ElementRef,
    Input,
    NgZone,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
    OnInit,
    Output,
    EventEmitter,
    OnChanges,
    AfterViewInit,
    OnDestroy,
    ChangeDetectorRef,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Component({
    selector: 'fi-rollup-content',
    templateUrl: './rollup-content.html',
    styleUrls: ['./rollup-content.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
    host: { class: 'fi-rollup-content-component', '[class.expandable]': 'canExpand$.value' },
})
export class RollupContentComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @ViewChild('rollupContent', { static: true }) rollupContentElement: ElementRef<HTMLElement>;
    @ViewChild('rollUpContentInner', { static: true }) rollUpContentInnerElement: ElementRef<HTMLElement>;

    @Input() opened = false;
    @Input() minHeight = 0;
    @Input() maxHeight = 0;

    @Output() contentOpened = new EventEmitter<void>();
    @Output() contentClosed = new EventEmitter<void>();

    private resizeObserver: ResizeObserver;

    constructor(
        private renderer: Renderer2,
        private ngZone: NgZone,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        // here we have both content and container heights not initialized (0px)
        if (this.minHeight > 0) {
            this.setMinHeight();
        }
        if (this.maxHeight > 0) {
            this.setMaxHeight();
        }
        if (this.opened) {
            this._setContentOpenedStyles();
        }
    }

    ngAfterViewInit(): void {
        // here we have content height initialized, container height not initialized (0px), so can check and set container height
        if (!this.opened && this.minHeight && !this.maxHeight) {
            this.setContainerHeight();
            // now parent component can determine if it is long post using canExpand$.value
        }

        setTimeout(() => this.updateCanExpand()); // now parent component can show 'show more button'

        this.setupResizeObserver();
    }

    ngOnDestroy(): void {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    setContainerHeight() {
        // temporary disable transition
        this.renderer.addClass(this.getRollupContentNativeElement(), 'notransition');
        this.updateCanExpand();
        this.renderer.removeClass(this.getRollupContentNativeElement(), 'notransition');
    }

    ngOnChanges(changes) {
        if (changes.opened) {
            if (this.opened) {
                this._setContentOpenedStyles();
            } else {
                this.removeSizeStyle();
                this.removeVisibleClass();
                if (this.minHeight) {
                    setTimeout(() => this.setContainerHeight());
                }
            }
        }
    }

    private getRollupContentNativeElement(): HTMLElement {
        return this.rollupContentElement.nativeElement;
    }

    private getRollupContentInnerNativeElement(): HTMLElement {
        return this.rollUpContentInnerElement.nativeElement;
    }

    private setMaxHeight() {
        this.renderer.setStyle(this.getRollupContentNativeElement(), 'maxHeight', this.maxHeight + 'px');
        this.renderer.setStyle(this.getRollupContentNativeElement(), 'height', 'auto');
    }

    private setMinHeight() {
        this.renderer.setStyle(this.getRollupContentNativeElement(), 'minHeight', this.minHeight + 'px');
    }

    private removeSizeStyle() {
        this.renderer.removeStyle(this.getRollupContentNativeElement(), 'height');
    }

    private addVisibleClass() {
        this.renderer.addClass(this.getRollupContentNativeElement(), 'roll-up-content-visible');
    }

    private removeVisibleClass() {
        this.renderer.removeClass(this.getRollupContentNativeElement(), 'roll-up-content-visible');
    }

    openContent(): Promise<void> {
        return new Promise<void>((resolve) => {
            this.opened = true;
            this.ngZone.runOutsideAngular(() => {
                const el = this.getRollupContentNativeElement();
                const innerEl = this.getRollupContentInnerNativeElement();
                // Remove auto height to get current pixel height
                this.renderer.setStyle(el, 'height', el.getBoundingClientRect().height + 'px');
                this.addVisibleClass();
                // Force reflow
                void el.offsetHeight;
                // Set target height to content height
                const targetHeight = innerEl.getBoundingClientRect().height;
                this.renderer.setStyle(el, 'height', targetHeight + 'px');
                // Remove maxHeight for animation
                this.renderer.removeStyle(el, 'maxHeight');
                setTimeout(() => {
                    // After transition, set height to auto for flexibility
                    this.renderer.setStyle(el, 'height', 'auto');
                    resolve();
                    this.ngZone.run(() => {
                        this.contentOpened.emit();
                    });
                }, 300);
            });
        });
    }

    private _setContentOpenedStyles() {
        this.addVisibleClass();
        this.removeSizeStyle();
    }

    closeContent(): Promise<any> {
        return new Promise<void>((resolve) => {
            this.opened = false;
            this.ngZone.runOutsideAngular(() => {
                const el = this.getRollupContentNativeElement();
                // Set current height to pixel value
                this.renderer.setStyle(el, 'height', el.getBoundingClientRect().height + 'px');
                // Force reflow
                void el.offsetHeight;
                this.removeVisibleClass();
                // Set target height: maxHeight > minHeight > 0
                let targetHeight = 0;
                if (this.maxHeight) {
                    targetHeight = this.maxHeight;
                } else if (this.minHeight) {
                    targetHeight = this.minHeight;
                }
                this.renderer.setStyle(el, 'height', targetHeight + 'px');
                setTimeout(() => {
                    // After transition, keep height at maxHeight/minHeight/0 as appropriate
                    this.renderer.setStyle(el, 'height', targetHeight ? targetHeight + 'px' : null);
                    resolve();
                    this.ngZone.run(() => {
                        this.contentClosed.emit();
                    });
                }, 300);
            });
        });
    }

    toggleContent(): Promise<any> {
        return this.opened ? this.closeContent() : this.openContent();
    }

    get state(): string {
        return this.opened ? 'opened' : 'closed';
    }

    canExpand$ = new BehaviorSubject<boolean>(false);
    logger() {}
    private updateCanExpand() {
        this.canExpand$.next(this.opened || this.getContentHeight() > this.getContainerHeight());
        this.cdr.detectChanges();
    }

    private getContentHeight(): number {
        return this.getRollupContentInnerNativeElement().getBoundingClientRect().height;
    }

    private getContainerHeight(): number {
        return this.getRollupContentNativeElement().getBoundingClientRect().height;
    }

    recalculateMinHeight() {
        setTimeout(() => {
            this.setContainerHeight();
        });
    }

    private setupResizeObserver(): void {
        this.resizeObserver = new ResizeObserver(() => {
            this.updateCanExpand();
        });

        this.resizeObserver.observe(this.getRollupContentNativeElement());
        this.resizeObserver.observe(this.getRollupContentInnerNativeElement());
    }
}
